import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import { eq } from 'tio-ui/utilities';
import Chart from 'tio-reimagined/components/chart';
import type { ChartDataPoint } from 'tio-common/types/chart';
import StatCard from 'tio-reimagined/components/stat-card';
import {
  PhArrowLeft,
  PhDownload,
  PhFunnel,
  PhCalendar,
  PhTrendUp,
  PhTrendDown,
  PhUsers,
  PhWarning,
  PhCheckCircle,
  PhArrowRight,
  PhChartBar,
  PhChartLine,
  PhChartPie,
} from 'ember-phosphor-icons';

// Mock pipeline report data
const REPORT_DATA = {
  summary: {
    totalInPipeline: 85,
    readyNowRatio: 0.14,
    avgTimeToReady: 18, // months
    riskyCoverage: 3, // positions with < 1 ready candidate
    projectedNeeds: 22, // next 24 months
    pipelineHealth: 'moderate',
  },
  pipelineFlow: {
    identified: 120,
    inProgram: 85,
    developing: 28,
    readyNow: 12,
    promoted: 8,
    attrition: 15,
  },
  stageDistribution: [
    { category: 'Emerging Leaders', value: 45 },
    { category: 'Developing', value: 28 },
    { category: 'Ready Now', value: 12 },
  ],
  monthlyProgress: [
    { category: 'Jul 2025', value: 68 },
    { category: 'Aug 2025', value: 72 },
    { category: 'Sep 2025', value: 75 },
    { category: 'Oct 2025', value: 78 },
    { category: 'Nov 2025', value: 82 },
    { category: 'Dec 2025', value: 85 },
  ],
  competencyScores: [
    { category: 'Strategic Thinking', value: 72 },
    { category: 'Team Leadership', value: 78 },
    { category: 'Change Management', value: 65 },
    { category: 'Financial Acumen', value: 58 },
    { category: 'Communication', value: 82 },
    { category: 'Decision Making', value: 70 },
  ],
  departmentPipeline: [
    { category: 'Nursing', value: 32 },
    { category: 'Clinical Services', value: 18 },
    { category: 'Operations', value: 15 },
    { category: 'Administration', value: 12 },
    { category: 'IT', value: 8 },
  ],
  roleRisk: [
    { role: 'Nurse Manager', coverage: 2.5, status: 'healthy', successors: 5, openings: 2 },
    { role: 'Clinical Director', coverage: 1.0, status: 'at-risk', successors: 2, openings: 2 },
    { role: 'Department Head', coverage: 0.5, status: 'critical', successors: 1, openings: 2 },
    { role: 'VP Operations', coverage: 1.5, status: 'moderate', successors: 3, openings: 2 },
    { role: 'Chief Nursing Officer', coverage: 2.0, status: 'healthy', successors: 2, openings: 1 },
  ],
  programROI: [
    { category: 'Leadership Foundations', value: 245 },
    { category: 'Executive Development', value: 312 },
    { category: 'New Manager Bootcamp', value: 178 },
    { category: 'Healthcare Management', value: 156 },
  ],
  timeInStage: [
    { category: 'Emerging (target: 12mo)', value: 8 },
    { category: 'Developing (target: 18mo)', value: 14 },
    { category: 'Ready Now (target: 6mo)', value: 4 },
  ],
  promotionTrend: [
    { category: 'Q1 2025', value: 3 },
    { category: 'Q2 2025', value: 5 },
    { category: 'Q3 2025', value: 4 },
    { category: 'Q4 2025', value: 8 },
  ],
  diversityMetrics: [
    { category: 'Women', value: 58 },
    { category: 'Men', value: 42 },
  ],
  diversityByStage: [
    { category: 'Emerging - Women', value: 62 },
    { category: 'Emerging - Men', value: 38 },
    { category: 'Developing - Women', value: 54 },
    { category: 'Developing - Men', value: 46 },
    { category: 'Ready Now - Women', value: 50 },
    { category: 'Ready Now - Men', value: 50 },
  ],
};

export default class CareerPathwaysAdminPipelineReport extends Component {
  @tracked selectedTimeframe = '12months';
  @tracked selectedDepartment = 'all';

  get data() {
    return REPORT_DATA;
  }

  get stageDistribution(): ChartDataPoint[] {
    return this.data.stageDistribution;
  }

  get monthlyProgress(): ChartDataPoint[] {
    return this.data.monthlyProgress;
  }

  get competencyScores(): ChartDataPoint[] {
    return this.data.competencyScores;
  }

  get departmentPipeline(): ChartDataPoint[] {
    return this.data.departmentPipeline;
  }

  get programROI(): ChartDataPoint[] {
    return this.data.programROI;
  }

  get timeInStage(): ChartDataPoint[] {
    return this.data.timeInStage;
  }

  get promotionTrend(): ChartDataPoint[] {
    return this.data.promotionTrend;
  }

  get diversityMetrics(): ChartDataPoint[] {
    return this.data.diversityMetrics;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0 pb-12 pipeline-report-charts">
      {{! Header with actions }}
      <div class="flex justify-between items-center mb-6">
        <div class="flex items-center gap-4">
          <a href="/reimagined/admin/leadership" class="text-gray-500 hover:text-gray-700">
            <PhArrowLeft @size={{20}} />
          </a>
          <div>
            <Heading @level={{1}} class="text-2xl font-bold text-gray-900">
              Leadership Pipeline Report
            </Heading>
            <Text @intent="muted">Comprehensive analysis of succession planning health</Text>
          </div>
        </div>
        <div class="flex gap-3">
          <Button @appearance="outlined">
            <PhCalendar @size={{18}} class="mr-2" />
            Last 12 Months
          </Button>
          <Button @appearance="outlined">
            <PhFunnel @size={{18}} class="mr-2" />
            All Departments
          </Button>
          <Button @intent="primary">
            <PhDownload @size={{18}} class="mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {{! Executive Summary Cards }}
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <StatCard
          @compact={{true}}
          @label="In Pipeline"
          @value={{this.data.summary.totalInPipeline}}
          @icon={{PhUsers}}
          @iconColor="violet"
          @trend="up"
          @trendText="+12% YoY"
        />

        <StatCard
          @compact={{true}}
          @label="Ready Now"
          @value={{this.data.pipelineFlow.readyNow}}
          @icon={{PhCheckCircle}}
          @iconColor="green"
          @secondaryText="14% of pipeline"
        />

        <StatCard
          @compact={{true}}
          @label="Promoted YTD"
          @value={{this.data.pipelineFlow.promoted}}
          @icon={{PhArrowRight}}
          @iconColor="teal"
          @trend="up"
          @trendText="+33% vs prior"
        />

        <StatCard
          @compact={{true}}
          @label="Avg Time to Ready"
          @value={{this.data.summary.avgTimeToReady}}
          @suffix="mo"
          @icon={{PhChartLine}}
          @iconColor="blue"
          @secondaryText="Target: 24mo"
        />

        <StatCard
          @compact={{true}}
          @label="At-Risk Roles"
          @value={{this.data.summary.riskyCoverage}}
          @valueColor="amber"
          @icon={{PhWarning}}
          @iconColor="amber"
          @secondaryText="Need attention"
        />

        <StatCard
          @compact={{true}}
          @label="Attrition"
          @value={{this.data.pipelineFlow.attrition}}
          @icon={{PhTrendDown}}
          @iconColor="red"
          @secondaryText="18% of pipeline"
        />
      </div>

      {{! Pipeline Funnel Visualization }}
      <div class="bg-white rounded-xl border border-gray-200 mb-6">
        <div class="flex items-center gap-2 p-5 border-b border-gray-100">
          <PhFunnel @size={{20}} class="text-violet-600" />
          <span class="font-medium text-gray-900">Pipeline Flow</span>
        </div>
        <div class="p-5">
          <div class="flex items-center justify-center py-4 gap-3 flex-wrap">
            <div class="text-center">
              <div
                class="px-8 py-4 bg-violet-100 dark:bg-violet-900/50 rounded-lg border-2 border-violet-200 dark:border-violet-700"
              >
                <div
                  class="text-3xl font-bold text-violet-700 dark:text-violet-300"
                >{{this.data.pipelineFlow.identified}}</div>
                <Text class="text-violet-600 dark:text-violet-400">Identified</Text>
              </div>
            </div>
            <PhArrowRight @size={{24}} class="text-gray-400" />
            <div class="text-center">
              <div
                class="px-8 py-4 bg-violet-200 dark:bg-violet-800/50 rounded-lg border-2 border-violet-300 dark:border-violet-600"
              >
                <div
                  class="text-3xl font-bold text-violet-700 dark:text-violet-300"
                >{{this.data.pipelineFlow.inProgram}}</div>
                <Text class="text-violet-600 dark:text-violet-400">In Program</Text>
              </div>
            </div>
            <PhArrowRight @size={{24}} class="text-gray-400" />
            <div class="text-center">
              <div
                class="px-8 py-4 bg-teal-100 dark:bg-teal-900/50 rounded-lg border-2 border-teal-200 dark:border-teal-700"
              >
                <div
                  class="text-3xl font-bold text-teal-700 dark:text-teal-300"
                >{{this.data.pipelineFlow.developing}}</div>
                <Text class="text-teal-600 dark:text-teal-400">Developing</Text>
              </div>
            </div>
            <PhArrowRight @size={{24}} class="text-gray-400" />
            <div class="text-center">
              <div
                class="px-8 py-4 bg-green-100 dark:bg-green-900/50 rounded-lg border-2 border-green-200 dark:border-green-700"
              >
                <div
                  class="text-3xl font-bold text-green-700 dark:text-green-300"
                >{{this.data.pipelineFlow.readyNow}}</div>
                <Text class="text-green-600 dark:text-green-400">Ready Now</Text>
              </div>
            </div>
            <PhArrowRight @size={{24}} class="text-gray-400" />
            <div class="text-center">
              <div
                class="px-8 py-4 bg-green-200 dark:bg-green-800/50 rounded-lg border-2 border-green-300 dark:border-green-600"
              >
                <div
                  class="text-3xl font-bold text-green-800 dark:text-green-200"
                >{{this.data.pipelineFlow.promoted}}</div>
                <Text class="text-green-700 dark:text-green-400">Promoted</Text>
              </div>
            </div>
          </div>
          <div class="flex justify-center mt-4 p-3 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
            <div class="flex items-center gap-2">
              <PhWarning @size={{18}} class="text-amber-600 dark:text-amber-400" />
              <Text class="text-amber-800 dark:text-amber-200">
                <strong>{{this.data.pipelineFlow.attrition}}</strong>
                candidates left pipeline (attrition rate: 18%)
              </Text>
            </div>
          </div>
        </div>
      </div>

      {{! Charts Grid - Row 1 }}
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {{! Pipeline Growth Over Time }}
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="flex items-center gap-2 p-5 border-b border-gray-100">
            <PhChartLine @size={{20}} class="text-teal-600" />
            <span class="font-medium text-gray-900">Pipeline Growth Trend</span>
          </div>
          <div class="p-5">
            {{#if this.monthlyProgress.length}}
              <Chart
                @data={{this.monthlyProgress}}
                @type="area"
                @xLabel="Month"
                @yLabel="Candidates"
                @height={{256}}
              />
            {{else}}
              <div class="h-64 flex items-center justify-center text-gray-500">
                No data available
              </div>
            {{/if}}
            <div class="mt-4 p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
              <Text @size="sm" class="text-green-800 dark:text-green-200">
                Pipeline has grown
                <strong>25%</strong>
                over the past 6 months
              </Text>
            </div>
          </div>
        </div>

        {{! Stage Distribution }}
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="flex items-center gap-2 p-5 border-b border-gray-100">
            <PhChartPie @size={{20}} class="text-violet-600" />
            <span class="font-medium text-gray-900">Stage Distribution</span>
          </div>
          <div class="p-5">
            {{#if this.stageDistribution.length}}
              <Chart @data={{this.stageDistribution}} @type="donut" />
            {{else}}
              <div class="h-64 flex items-center justify-center text-gray-500">
                No data available
              </div>
            {{/if}}
            <div class="flex justify-center gap-6 mt-4">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded-full bg-violet-500"></div>
                <Text @size="sm" class="dark:text-gray-200">Emerging (53%)</Text>
              </div>
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded-full bg-teal-500"></div>
                <Text @size="sm" class="dark:text-gray-200">Developing (33%)</Text>
              </div>
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded-full bg-green-500"></div>
                <Text @size="sm" class="dark:text-gray-200">Ready Now (14%)</Text>
              </div>
            </div>
          </div>
        </div>
      </div>

      {{! Charts Grid - Row 2 }}
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {{! Competency Gaps }}
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="flex items-center gap-2 p-5 border-b border-gray-100">
            <PhChartBar @size={{20}} class="text-blue-600" />
            <span class="font-medium text-gray-900">Leadership Competency Scores</span>
          </div>
          <div class="p-5">
            {{#if this.competencyScores.length}}
              <Chart
                @data={{this.competencyScores}}
                @type="horizontalBar"
                @xLabel="Score %"
                @yLabel="Competency"
              />
            {{else}}
              <div class="h-64 flex items-center justify-center text-gray-500">
                No data available
              </div>
            {{/if}}
            <div class="mt-4 p-3 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
              <Text @size="sm" class="text-amber-800 dark:text-amber-200">
                <strong>Financial Acumen</strong>
                and
                <strong>Change Management</strong>
                are largest gaps (target: 80%)
              </Text>
            </div>
          </div>
        </div>

        {{! Department Pipeline }}
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="flex items-center gap-2 p-5 border-b border-gray-100">
            <PhChartBar @size={{20}} class="text-violet-600" />
            <span class="font-medium text-gray-900">Pipeline by Department</span>
          </div>
          <div class="p-5">
            {{#if this.departmentPipeline.length}}
              <Chart
                @data={{this.departmentPipeline}}
                @type="bar"
                @xLabel="Department"
                @yLabel="Candidates"
                @height={{256}}
              />
            {{else}}
              <div class="h-64 flex items-center justify-center text-gray-500">
                No data available
              </div>
            {{/if}}
            <div class="mt-4 p-3 bg-violet-50 dark:bg-violet-900/30 rounded-lg">
              <Text @size="sm" class="text-violet-800 dark:text-violet-200">
                <strong>Nursing</strong>
                has strongest pipeline (38% of total)
              </Text>
            </div>
          </div>
        </div>
      </div>

      {{! Charts Grid - Row 3 }}
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {{! Program ROI }}
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="flex items-center gap-2 p-5 border-b border-gray-100">
            <PhChartBar @size={{20}} class="text-green-600" />
            <span class="font-medium text-gray-900">Program ROI %</span>
          </div>
          <div class="p-5">
            {{#if this.programROI.length}}
              <Chart
                @data={{this.programROI}}
                @type="horizontalBar"
                @xLabel="ROI %"
                @yLabel="Program"
              />
            {{else}}
              <div class="h-48 flex items-center justify-center text-gray-500">
                No data available
              </div>
            {{/if}}
          </div>
        </div>

        {{! Promotions Trend }}
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="flex items-center gap-2 p-5 border-b border-gray-100">
            <PhTrendUp @size={{20}} class="text-teal-600" />
            <span class="font-medium text-gray-900">Quarterly Promotions</span>
          </div>
          <div class="p-5">
            {{#if this.promotionTrend.length}}
              <Chart
                @data={{this.promotionTrend}}
                @type="line"
                @xLabel="Quarter"
                @yLabel="Promotions"
                @height={{192}}
              />
            {{else}}
              <div class="h-48 flex items-center justify-center text-gray-500">
                No data available
              </div>
            {{/if}}
          </div>
        </div>

        {{! Diversity }}
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="flex items-center gap-2 p-5 border-b border-gray-100">
            <PhUsers @size={{20}} class="text-violet-600" />
            <span class="font-medium text-gray-900">Gender Diversity</span>
          </div>
          <div class="p-5">
            {{#if this.diversityMetrics.length}}
              <Chart @data={{this.diversityMetrics}} @type="pie" />
            {{else}}
              <div class="h-48 flex items-center justify-center text-gray-500">
                No data available
              </div>
            {{/if}}
          </div>
        </div>
      </div>

      {{! Role Coverage Risk Table }}
      <div class="bg-white rounded-xl border border-gray-200 mb-6">
        <div class="flex items-center gap-2 p-5 border-b border-gray-100">
          <PhWarning @size={{20}} class="text-amber-500" />
          <span class="font-medium text-gray-900">Succession Coverage by Role</span>
        </div>
        <div class="p-5">
          <table class="w-full">
            <thead>
              <tr class="border-b border-gray-200">
                <th class="text-left py-3 px-4 font-semibold text-gray-700">Role</th>
                <th class="text-center py-3 px-4 font-semibold text-gray-700">Ready Successors</th>
                <th class="text-center py-3 px-4 font-semibold text-gray-700">Projected Openings</th>
                <th class="text-center py-3 px-4 font-semibold text-gray-700">Coverage Ratio</th>
                <th class="text-center py-3 px-4 font-semibold text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {{#each this.data.roleRisk as |role|}}
                <tr class="border-b border-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td class="py-3 px-4">
                    <Text class="font-medium text-gray-900">{{role.role}}</Text>
                  </td>
                  <td class="py-3 px-4 text-center">
                    <Text class="dark:text-gray-200">{{role.successors}}</Text>
                  </td>
                  <td class="py-3 px-4 text-center">
                    <Text class="dark:text-gray-200">{{role.openings}}</Text>
                  </td>
                  <td class="py-3 px-4 text-center">
                    <Text class="font-semibold">{{role.coverage}}x</Text>
                  </td>
                  <td class="py-3 px-4 text-center">
                    {{#if (eq role.status "healthy")}}
                      <Chip @intent="success">Healthy</Chip>
                    {{else if (eq role.status "moderate")}}
                      <Chip @intent="warning">Moderate</Chip>
                    {{else if (eq role.status "at-risk")}}
                      <Chip @intent="warning">At Risk</Chip>
                    {{else}}
                      <Chip @intent="danger">Critical</Chip>
                    {{/if}}
                  </td>
                </tr>
              {{/each}}
            </tbody>
          </table>
        </div>
      </div>

      {{! Key Insights & Recommendations }}
      <div class="bg-white rounded-xl border border-gray-200">
        <div class="flex items-center gap-2 p-5 border-b border-gray-100">
          <PhCheckCircle @size={{20}} class="text-teal-600" />
          <span class="font-medium text-gray-900">Key Insights & Recommendations</span>
        </div>
        <div class="p-5">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="space-y-4">
              <Heading @level={{3}} class="text-lg font-semibold text-gray-900">Strengths</Heading>
              <div class="space-y-3">
                <div class="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
                  <PhCheckCircle @size={{20}} class="text-green-600 dark:text-green-400 mt-0.5" />
                  <div>
                    <Text class="font-medium text-green-900 dark:text-green-200">Strong pipeline
                      growth</Text>
                    <Text @size="sm" class="text-green-800 dark:text-green-300">25% increase in 6
                      months exceeds 15% target</Text>
                  </div>
                </div>
                <div class="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
                  <PhCheckCircle @size={{20}} class="text-green-600 dark:text-green-400 mt-0.5" />
                  <div>
                    <Text class="font-medium text-green-900 dark:text-green-200">Accelerated
                      development</Text>
                    <Text @size="sm" class="text-green-800 dark:text-green-300">Avg time to ready
                      (18mo) beats 24mo target by 25%</Text>
                  </div>
                </div>
                <div class="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
                  <PhCheckCircle @size={{20}} class="text-green-600 dark:text-green-400 mt-0.5" />
                  <div>
                    <Text class="font-medium text-green-900 dark:text-green-200">High program ROI</Text>
                    <Text @size="sm" class="text-green-800 dark:text-green-300">Executive
                      Development program shows 312% ROI</Text>
                  </div>
                </div>
              </div>
            </div>
            <div class="space-y-4">
              <Heading @level={{3}} class="text-lg font-semibold text-gray-900">Areas for Action</Heading>
              <div class="space-y-3">
                <div class="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
                  <PhWarning @size={{20}} class="text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <Text class="font-medium text-amber-900 dark:text-amber-200">Department Head
                      coverage critical</Text>
                    <Text @size="sm" class="text-amber-800 dark:text-amber-300">Only 0.5x
                      coverage—prioritize identifying candidates</Text>
                  </div>
                </div>
                <div class="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
                  <PhWarning @size={{20}} class="text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <Text class="font-medium text-amber-900 dark:text-amber-200">Financial acumen
                      gap</Text>
                    <Text @size="sm" class="text-amber-800 dark:text-amber-300">58% avg score
                      (target 80%)—add finance training</Text>
                  </div>
                </div>
                <div class="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/30 rounded-lg">
                  <PhWarning @size={{20}} class="text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <Text class="font-medium text-amber-900 dark:text-amber-200">IT department
                      underrepresented</Text>
                    <Text @size="sm" class="text-amber-800 dark:text-amber-300">Only 8 in
                      pipeline—increase identification efforts</Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}
